import { useInfiniteQuery } from '@tanstack/react-query';

import queryKeys from '@pray/shared/queryKeys';
import api from '@pray/shared/utils/api';
import storage from '@pray/shared/utils/storage';

import type { ConversationPlatform, ConversationsListResponse, GetConversationsParams } from './types';

export async function getConversations({
  artistId,
  limit = 10,
  startAt,
  sortBy,
  dateStart,
  dateEnd,
  platform,
  hideEmpty,
}: GetConversationsParams): Promise<ConversationsListResponse> {
  if (!artistId) throw Error('Artist ID is required');

  const params: Record<string, string | number> = { limit };

  if (startAt) {
    params.cursor = startAt;
  }

  if (sortBy) {
    params.sort_by = sortBy;
  }

  if (dateStart) {
    params.date_start = dateStart;
  }

  if (dateEnd) {
    params.date_end = dateEnd;
  }

  if (platform && platform.length > 0) {
    params.platform = platform.join(',');
  }

  if (hideEmpty !== undefined) {
    params.hide_empty = String(hideEmpty);
  }

  const url = `/web/studio/artists/${artistId}/conversations`;
  const response = await api.executeGetRequest<ConversationsListResponse>(url, params);

  return response.data;
}

export function useChatbotConversations(
  artistId: string,
  limit = 10,
  sortBy?: string,
  dateStart?: string,
  dateEnd?: string,
  platform?: ConversationPlatform[],
  hideEmpty?: boolean
) {
  const user = storage.getUser();

  const query = useInfiniteQuery({
    initialPageParam: undefined,
    enabled: !!artistId && !!user?.is_registered,
    queryKey: queryKeys.artistChatbotConversations(artistId, {
      limit,
      sortBy,
      dateStart,
      dateEnd,
      platform,
      hideEmpty,
    }),
    queryFn: ({ pageParam }) =>
      getConversations({
        artistId,
        limit,
        startAt: pageParam,
        sortBy: sortBy as 'most_recent' | 'most_messages' | undefined,
        dateStart,
        dateEnd,
        platform,
        hideEmpty,
      }),
    getNextPageParam: (lastPage) => {
      // Only return next page cursor if we have data and a valid cursor
      if (!lastPage.data || lastPage.data.length === 0) {
        return undefined;
      }
      // If last_item_identifier is null or empty, no more pages
      if (!lastPage.last_item_identifier) {
        return undefined;
      }
      return lastPage.last_item_identifier;
    },
  });

  return query;
}
